import { useState, useEffect } from 'react';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useAuth } from '../context/AuthContext';
import { Calendar, Moon, Clock, Anchor, Award, Star, ExternalLink, TrendingUp, TrendingDown, Link as LinkIcon, Calculator, PiggyBank, Wallet, Rewind, Sparkles, Copy, Gem, Crown } from 'lucide-react';
import toast from 'react-hot-toast';
import { CurrencyIcon } from '../components/CurrencyIcon';
import { Spade } from 'lucide-react';
import { motion } from 'framer-motion';
import { parseISO, isAfter, differenceInDays, startOfYear, endOfYear } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { HighlightsSkeleton } from '../components/LoadingSkeleton';
import { REWIND_DEBUG_MODE } from '../config';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { isCarnivalCruiseLine, CARNIVAL_PLATINUM_BOOST_STARS } from '../utils/loyaltyUtils';

interface EffectiveLoyaltyStatus {
  cruiseLineName: string;
  actualStatusPoints: number;
  effectiveStatusPoints: number;
  tierName: string | null;
  isMatched: boolean;
  loyaltyNumber?: string;
  matchedFrom?: {
    cruiseLineName: string;
    tierName: string;
  };
  /** Secondary sailing-count track (e.g. Princess Captain's Circle) */
  secondarySailingCount?: number;
  secondaryTierName?: string | null;
  /** True when the cruise-count tier is better than the nights tier */
  secondaryTrackWins?: boolean;
  /** Grandfathered tier (e.g. Carnival Rewards legacy Diamond / Platinum) */
  grandfatheredTier?: string;
  grandfatheredExpiry?: string;
  legacyPoints?: number;
  cycleEndDate?: string;
}

const Highlights = () => {
  const { cruises, loading } = useCruises();
  const { getLoyaltyCruiseLines, getLoyaltyTiersForCruiseLine, getLoyaltyTierName, getTierMinValueByName, getLoyaltyMatches, getLoyaltyCalculationRules, getSecondaryTrackTier, loading: metadataLoading } = useCruiseMetadata();
  const { loyaltyStatus } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency } = useDisplayPreferences();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [effectiveLoyaltyStatuses, setEffectiveLoyaltyStatuses] = useState<EffectiveLoyaltyStatus[]>([]);
  const [stats, setStats] = useState({
    upcomingCruises: 0,
    upcomingNights: 0,
    thisYearNights: 0,
    lifetimeNights: 0,
    totalPorts: 0,
    uniquePorts: new Set<string>(),
    cruiseLines: new Set<string>(),
    ships: new Set<string>(),
    casinoComps: {
      upcoming: 0,
      past: 0,
      total: 0,
      upcomingValue: 0,
      pastValue: 0,
      totalValue: 0
    },
    yearlyGambling: {
      wins: 0,
      losses: 0,
      net: 0
    },
    annualFinancials: {
      cruiseCompValue: 0,
      totalExpenses: 0,
      netCost: 0
    }
  });

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500); // 500ms minimum skeleton display

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!loading && !metadataLoading) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [loading, metadataLoading]);

  useEffect(() => {
    if (cruises) {
      const now = new Date();
      const yearStart = startOfYear(now);
      const yearEnd = endOfYear(now);

      const newStats = cruises.reduce((acc, cruise) => {
        const departureDate = parseISO(cruise.departureDate);
        const returnDate = parseISO(cruise.returnDate);
        const nights = differenceInDays(returnDate, departureDate);
        const isUpcoming = isAfter(departureDate, now);
        const isCompletedThisYear = cruise.completed && 
          returnDate >= yearStart && 
          returnDate <= yearEnd;

        // Casino comps tracking
        if (cruise.isComplimentaryCasinoSailing) {
          acc.casinoComps.total++;
          acc.casinoComps.totalValue += Number(cruise.cruiseCost || 0);
          
          if (isUpcoming) {
            acc.casinoComps.upcoming++;
            acc.casinoComps.upcomingValue += Number(cruise.cruiseCost || 0);
          } else {
            acc.casinoComps.past++;
            acc.casinoComps.pastValue += Number(cruise.cruiseCost || 0);
          }
        }

        // Upcoming cruises
        if (isUpcoming) {
          acc.upcomingCruises++;
          acc.upcomingNights += nights;
        } else {
          // Past cruises
          if (departureDate >= yearStart && returnDate <= yearEnd) {
            acc.thisYearNights += nights;
          }
          acc.lifetimeNights += nights;
        }

        // Annual gambling tracking (only for completed cruises this year)
        if (isCompletedThisYear) {
          acc.yearlyGambling.wins += Number(cruise.gamblingWins || 0);
          acc.yearlyGambling.losses += Number(cruise.gamblingLosses || 0);
        }

        // Annual financial tracking (only for completed cruises this year)
        if (isCompletedThisYear) {
          // Annual cruise comp value (raw cruise cost for casino comps)
          if (cruise.isComplimentaryCasinoSailing) {
            acc.annualFinancials.cruiseCompValue += Number(cruise.cruiseCost || 0);
          }

          // Total annual cruise expenses
          const cruiseCost = cruise.isComplimentaryCasinoSailing ? 0 : Number(cruise.cruiseCost || 0);
          const portTaxesFees = Number(cruise.portTaxesFees || 0);
          const additionalCosts = Number(cruise.additionalCosts || 0);
          const onboardExpenses = Number(cruise.onboardExpenses || 0);
          
          acc.annualFinancials.totalExpenses += cruiseCost + portTaxesFees + additionalCosts + onboardExpenses;
        }

        // Track unique ports, cruise lines, and ships
        cruise.ports?.forEach(port => acc.uniquePorts.add(port.name));
        acc.cruiseLines.add(cruise.cruiseLine);
        // Use lowercase for case-insensitive ship name matching
        acc.ships.add(cruise.shipName.toLowerCase());
        acc.totalPorts += cruise.ports?.length || 0;

        return acc;
      }, {
        upcomingCruises: 0,
        upcomingNights: 0,
        thisYearNights: 0,
        lifetimeNights: 0,
        totalPorts: 0,
        uniquePorts: new Set<string>(),
        cruiseLines: new Set<string>(),
        ships: new Set<string>(),
        casinoComps: {
          upcoming: 0,
          past: 0,
          total: 0,
          upcomingValue: 0,
          pastValue: 0,
          totalValue: 0
        },
        yearlyGambling: {
          wins: 0,
          losses: 0,
          net: 0
        },
        annualFinancials: {
          cruiseCompValue: 0,
          totalExpenses: 0,
          netCost: 0
        }
      });

      // Calculate derived values
      newStats.yearlyGambling.net = newStats.yearlyGambling.wins - newStats.yearlyGambling.losses;
      newStats.annualFinancials.netCost = newStats.annualFinancials.totalExpenses - newStats.yearlyGambling.net;

      setStats(newStats);
    }
  }, [cruises]);

  // Calculate effective loyalty statuses including matches
  useEffect(() => {
    if (!loyaltyStatus.length || metadataLoading) {
      // If no statuses or still loading, just use actual statuses
      const actualStatuses: EffectiveLoyaltyStatus[] = loyaltyStatus.map(status => {
        let pts = status.status;
        if (isCarnivalCruiseLine(status.cruiseLineName) && status.grandfatheredTier === 'Platinum' && pts < CARNIVAL_PLATINUM_BOOST_STARS) {
          pts = CARNIVAL_PLATINUM_BOOST_STARS;
        }
        return {
          cruiseLineName: status.cruiseLineName,
          actualStatusPoints: pts,
          effectiveStatusPoints: pts,
          tierName: getLoyaltyTierName(status.cruiseLineName, pts, status.grandfatheredTier),
          isMatched: false,
          loyaltyNumber: status.loyaltyNumber,
          grandfatheredTier: status.grandfatheredTier,
          grandfatheredExpiry: status.grandfatheredExpiry,
          legacyPoints: status.legacyPoints,
          cycleEndDate: status.cycleEndDate
        };
      });
      setEffectiveLoyaltyStatuses(actualStatuses);
      return;
    }

    // Calculate effective loyalty statuses including matches and secondary tracks
    const effectiveStatusMap = new Map<string, EffectiveLoyaltyStatus>();

    // First, populate with actual loyalty statuses
    loyaltyStatus.forEach(status => {
      let actualPoints = status.status;
      const rules = getLoyaltyCalculationRules(status.cruiseLineName);
      const isCarnival = isCarnivalCruiseLine(status.cruiseLineName);
      const isStars = rules?.method === 'manual' || rules?.supportsGrandfathering;
      if (isCarnival && status.grandfatheredTier === 'Platinum' && (isStars || actualPoints < CARNIVAL_PLATINUM_BOOST_STARS)) {
        actualPoints = Math.max(CARNIVAL_PLATINUM_BOOST_STARS, actualPoints);
      }

      const tierName = getLoyaltyTierName(status.cruiseLineName, actualPoints, status.grandfatheredTier);
      effectiveStatusMap.set(status.cruiseLineName, {
        cruiseLineName: status.cruiseLineName,
        actualStatusPoints: actualPoints,
        effectiveStatusPoints: actualPoints,
        tierName,
        isMatched: false,
        loyaltyNumber: status.loyaltyNumber,
        grandfatheredTier: status.grandfatheredTier,
        grandfatheredExpiry: status.grandfatheredExpiry,
        legacyPoints: status.legacyPoints,
        cycleEndDate: status.cycleEndDate
      });
    });

    // Then, check for matching rules that might grant higher statuses
    loyaltyStatus.forEach(userStatus => {
      const userTierName = getLoyaltyTierName(userStatus.cruiseLineName, userStatus.status, userStatus.grandfatheredTier);
      if (!userTierName) return;

      // Get matches for this user's status using the new helper function
      const matches = getLoyaltyMatches(userStatus.cruiseLineName, userTierName);

      matches.forEach(match => {
        const matchedTierMinValue = getTierMinValueByName(match.toCruiseLine, match.toTierName);
        if (matchedTierMinValue === null) return;

        const currentEffectiveStatus = effectiveStatusMap.get(match.toCruiseLine);
        
        // If no current status for this cruise line, or the matched status is higher
        if (!currentEffectiveStatus || matchedTierMinValue > currentEffectiveStatus.effectiveStatusPoints) {
          const actualPoints = currentEffectiveStatus ? currentEffectiveStatus.actualStatusPoints : 0;
          effectiveStatusMap.set(match.toCruiseLine, {
            cruiseLineName: match.toCruiseLine,
            actualStatusPoints: actualPoints,
            effectiveStatusPoints: matchedTierMinValue,
            tierName: match.toTierName,
            isMatched: true,
            loyaltyNumber: currentEffectiveStatus?.loyaltyNumber,
            grandfatheredTier: currentEffectiveStatus?.grandfatheredTier,
            grandfatheredExpiry: currentEffectiveStatus?.grandfatheredExpiry,
            legacyPoints: currentEffectiveStatus?.legacyPoints,
            cycleEndDate: currentEffectiveStatus?.cycleEndDate,
            matchedFrom: {
              cruiseLineName: userStatus.cruiseLineName,
              tierName: userTierName
            }
          });
        }
      });
    });

    // Secondary track check: for programs like Princess, also count completed in-app sailings
    // and upgrade the tier if the cruise-count track gives a better result.
    if (cruises) {
      effectiveStatusMap.forEach((entry, cruiseLineName) => {
        const rules = getLoyaltyCalculationRules(cruiseLineName);
        if (!rules?.secondaryTrack) return;

        // Count completed in-app sailings for this cruise line
        const completedSailings = cruises.filter(
          c => c.cruiseLine === cruiseLineName && c.completed
        ).length;

        const secondaryTierName = getSecondaryTrackTier(cruiseLineName, completedSailings);

        // Determine which tier is "better" by comparing their min_value thresholds
        const nightsTierMin = getTierMinValueByName(cruiseLineName, entry.tierName || '') ?? 0;

        // secondaryTrack uses its own secondaryTiers, so we compute the min from those directly
        const secondaryTierMin = rules.secondaryTiers?.find(t => t.name === secondaryTierName)?.min_value ?? 0;

        const secondaryWins = secondaryTierName !== null && secondaryTierMin > nightsTierMin;

        effectiveStatusMap.set(cruiseLineName, {
          ...entry,
          secondarySailingCount: completedSailings,
          secondaryTierName,
          secondaryTrackWins: secondaryWins,
          // If secondary track wins, surface the secondary tier name in the main tierName
          tierName: secondaryWins ? secondaryTierName : entry.tierName,
        });
      });
    }

    // Convert map to array and sort by cruise line name
    const effectiveStatuses = Array.from(effectiveStatusMap.values())
      .sort((a, b) => a.cruiseLineName.localeCompare(b.cruiseLineName));

    setEffectiveLoyaltyStatuses(effectiveStatuses);
  }, [loyaltyStatus, metadataLoading, cruises, getLoyaltyTierName, getTierMinValueByName, getLoyaltyMatches, getLoyaltyCalculationRules, getSecondaryTrackTier]);

  const StatCard = ({ icon: Icon, title, value, subtitle, color }: {
    icon: any,
    title: string,
    value: number | string,
    subtitle?: string,
    color: string
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg shadow-md p-6 ${color} overflow-hidden relative`}
    >
      <div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-10">
        <Icon className="h-24 w-24" />
      </div>
      <div className="relative z-10">
        <div className="flex items-center mb-4">
          <Icon className="h-6 w-6 mr-2" />
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <div className="text-3xl font-bold mb-1">{value}</div>
        {subtitle && <p className="text-sm opacity-75">{subtitle}</p>}
      </div>
    </motion.div>
  );

  // Get loyalty programs for cruise lines where the user has an effective status
  const getLoyaltyProgramsForDisplay = () => {
    const loyaltyCruiseLines = getLoyaltyCruiseLines();
    const effectiveCruiseLines = effectiveLoyaltyStatuses.map(status => status.cruiseLineName);
    
    return loyaltyCruiseLines.filter(cl => effectiveCruiseLines.includes(cl.name));
  };

  // Calculate unified next tier information (handles primary, secondary tracks, and grandfathered statuses)
  const getNextTierInfo = (
    cruiseLine: string, 
    effectiveTierName: string | null, 
    actualStatusPoints: number, 
    secondarySailingCount?: number,
    grandfatheredTier?: string
  ) => {
    const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine);
    const rules = getLoyaltyCalculationRules(cruiseLine);
    if (!loyaltyData) return null;

    // Grandfathered Diamond is lifetime top tier
    if (grandfatheredTier === 'Diamond') {
      return { isMaxTier: true, isLifetime: true, unit: loyaltyData.unit };
    }

    const sortedTiers = [...loyaltyData.tiers].sort((a, b) => a.min_value - b.min_value);
    
    // Find the next tier based on current effective status
    let nextTierIndex = 0;
    if (effectiveTierName) {
        const currentTierIndex = sortedTiers.findIndex(t => t.name.toLowerCase() === effectiveTierName.toLowerCase());
        if (currentTierIndex !== -1) {
            nextTierIndex = currentTierIndex + 1;
        }
    }
    
    if (nextTierIndex >= sortedTiers.length || sortedTiers.length === 0) {
        return { isMaxTier: true, isLifetime: false, unit: loyaltyData.unit };
    }
    
    const nextTier = sortedTiers[nextTierIndex];
    
    // Primary track points needed
    const pointsNeeded = Math.max(0, nextTier.min_value - actualStatusPoints);
    
    // Secondary track points needed
    let sailingsNeeded = undefined;
    if (secondarySailingCount !== undefined && rules?.secondaryTiers) {
        const secondaryTier = rules.secondaryTiers.find(t => t.name === nextTier.name);
        if (secondaryTier) {
            sailingsNeeded = Math.max(0, secondaryTier.min_value - secondarySailingCount);
        }
    }
    
    return {
        isMaxTier: false,
        nextTierName: nextTier.name,
        pointsNeeded,
        sailingsNeeded,
        unit: loyaltyData.unit
    };
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return (
      <HighlightsSkeleton />
    );
  }

  const currentYear = new Date().getFullYear();
  const loyaltyProgramsForDisplay = getLoyaltyProgramsForDisplay();

  // Check if Rewind banner should be shown (December only, or debug mode)
  const currentMonth = new Date().getMonth(); // 0-indexed, so 11 = December
  const showRewindBanner = currentMonth === 11 || REWIND_DEBUG_MODE;

  return (
    <motion.div 
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Cruise Highlights</h1>
        <p className="text-gray-600 mt-1">Your cruise journey at a glance</p>
      </div>

      {/* Cruising Along Rewind Banner */}
      {showRewindBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate(`/rewind/${currentYear}`)}
        >
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 rounded-full p-3 relative">
                <Rewind className="h-8 w-8 text-white" />
                <Sparkles className="h-4 w-4 text-white absolute -top-1 -right-1" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Cruising Along Rewind {currentYear}
                </h2>
                <p className="text-white/90 text-lg">
                  It's that time of year again for your year in review!
                </p>
              </div>
            </div>
            <div className="text-white">
              <ExternalLink className="h-6 w-6" />
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Calendar}
          title="Upcoming Cruises"
          value={stats.upcomingCruises}
          subtitle="adventures await"
          color="text-primary-900"
        />
        <StatCard
          icon={Moon}
          title="Upcoming Nights"
          value={stats.upcomingNights}
          subtitle="nights at sea planned"
          color="text-purple-900"
        />
        <StatCard
          icon={Clock}
          title="This Year"
          value={stats.thisYearNights}
          subtitle={`nights at sea in ${currentYear}`}
          color="text-secondary-900"
        />
        <StatCard
          icon={Anchor}
          title="Lifetime"
          value={stats.lifetimeNights}
          subtitle="total nights at sea"
          color="text-indigo-900"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg shadow-md p-6 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">Port Statistics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span>Total Port Visits</span>
              <span className="text-2xl font-bold">{stats.totalPorts}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Unique Ports</span>
              <span className="text-2xl font-bold">{stats.uniquePorts.size}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-indigo-900 to-indigo-700 rounded-lg shadow-md p-6 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">Cruise Experience</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span>Cruise Lines</span>
              <span className="text-2xl font-bold">{stats.cruiseLines.size}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Different Ships</span>
              <span className="text-2xl font-bold">{stats.ships.size}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Loyalty Programs Section */}
      {loyaltyProgramsForDisplay.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-secondary-700 to-secondary-900 rounded-lg shadow-md p-6 text-white"
        >
          <div className="flex items-center mb-4">
            <Award className="h-6 w-6 mr-2" />
            <h3 className="text-xl font-semibold">Loyalty Programs</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loyaltyProgramsForDisplay.map((cruiseLine) => {
              const effectiveStatus = effectiveLoyaltyStatuses.find(
                status => status.cruiseLineName === cruiseLine.name
              );
              
              const nextTierInfo = effectiveStatus 
                ? getNextTierInfo(
                    cruiseLine.name, 
                    effectiveStatus.tierName, 
                    effectiveStatus.actualStatusPoints, 
                    effectiveStatus.secondarySailingCount,
                    effectiveStatus.grandfatheredTier
                  )
                : null;

              const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine.name);
              const programName = loyaltyData?.programName;
              
              return (
                <div key={cruiseLine.name} className="bg-teal-700/30 border border-teal-600/50 shadow-inner rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-teal-100">{cruiseLine.name}</h4>
                      {programName && (
                        <p className="text-sm text-teal-200">{programName}</p>
                      )}
                    </div>
                    {effectiveStatus && (
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-accent-500 mr-1" />
                        <span className="text-sm text-teal-200">
                          {effectiveStatus.actualStatusPoints.toLocaleString()} {cruiseLine.loyaltyUnit || 'points'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {effectiveStatus ? (
                    <div className="space-y-2">
                      <div className="text-teal-200">
                        Current Status: <span className={`font-medium ${effectiveStatus.isMatched ? 'text-green-300' : 'text-yellow-300'}`}>
                          {effectiveStatus.tierName || 'Unknown Tier'}
                          {effectiveStatus.isMatched && (
                            <span className="ml-2 inline-flex items-center">
                              <LinkIcon className="h-3 w-3 mr-1" />
                              <span className="text-xs">(Matched)</span>
                            </span>
                          )}
                        </span>
                      </div>

                      {/* Grandfathered Tier Badges */}
                      {effectiveStatus.grandfatheredTier && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          {effectiveStatus.grandfatheredTier === 'Diamond' ? (
                            <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-900/60 text-purple-200 border border-purple-500/40">
                              <Gem className="h-3 w-3 mr-1 text-purple-300" />
                              Lifetime Diamond (Exempt from requalification)
                            </span>
                          ) : effectiveStatus.grandfatheredTier === 'Platinum' ? (
                            <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-900/60 text-indigo-200 border border-indigo-500/40">
                              <Award className="h-3 w-3 mr-1 text-indigo-300" />
                              Grandfathered Platinum {effectiveStatus.grandfatheredExpiry ? `(Protected through ${effectiveStatus.grandfatheredExpiry} · +10k Boost)` : '(+10k Boost)'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-200 border border-amber-500/40">
                              <Crown className="h-3 w-3 mr-1 text-amber-300" />
                              Grandfathered {effectiveStatus.grandfatheredTier}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Preserved Legacy VIFP Points/Days */}
                      {effectiveStatus.legacyPoints !== undefined && effectiveStatus.legacyPoints > 0 && (
                        <div className="text-xs text-teal-300/90 flex items-center gap-1.5 bg-teal-900/40 border border-teal-700/40 rounded px-2.5 py-1">
                          <Clock className="h-3.5 w-3.5 text-teal-400 shrink-0" />
                          <span>Preserved Historical VIFP: <strong className="text-white">{effectiveStatus.legacyPoints}</strong> sailing days</span>
                        </div>
                      )}

                      {/* Active Cycle Window */}
                      {effectiveStatus.cycleEndDate && (
                        <div className="text-xs text-teal-300/80 flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-teal-400 shrink-0" />
                          <span>Collection Cycle ends <strong className="text-teal-200">{effectiveStatus.cycleEndDate}</strong></span>
                        </div>
                      )}
                      
                      {/* Show matched from information */}
                      {effectiveStatus.isMatched && effectiveStatus.matchedFrom && (
                        <div className="text-sm text-green-200 bg-green-900/30 rounded px-2 py-1">
                          <LinkIcon className="h-3 w-3 inline mr-1" />
                          Matched from {effectiveStatus.matchedFrom.cruiseLineName} {effectiveStatus.matchedFrom.tierName}
                        </div>
                      )}

                      {/* Secondary track info (e.g. Princess cruise-count track) */}
                      {effectiveStatus.secondarySailingCount !== undefined && (
                        <div className="text-sm text-teal-300 bg-teal-900/20 border border-teal-700/30 rounded px-2 py-1">
                          <span className="font-medium">{effectiveStatus.actualStatusPoints.toLocaleString()}</span> {cruiseLine.loyaltyUnit || 'nights'}
                          {' · '}
                          <span className="font-medium">{effectiveStatus.secondarySailingCount}</span> sailing{effectiveStatus.secondarySailingCount !== 1 ? 's' : ''}
                          {effectiveStatus.secondaryTrackWins && (
                            <span className="ml-1 text-xs text-yellow-300">(cruise count wins)</span>
                          )}
                        </div>
                      )}
                      
                      {/* Loyalty Number */}
                      {effectiveStatus.loyaltyNumber && (
                        <div className="text-sm text-teal-200 mt-2 flex items-center">
                          <span className="mr-2">Loyalty #: <span className="font-medium text-white">{effectiveStatus.loyaltyNumber}</span></span>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(effectiveStatus.loyaltyNumber || '');
                              toast.success('Loyalty number copied to clipboard');
                            }}
                            className="p-1 hover:bg-teal-600/50 rounded transition-colors text-teal-300 hover:text-white"
                            title="Copy to clipboard"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      
                      {/* Next Tier Countdown */}
                      {nextTierInfo && (
                        <div className="mt-3 pt-3 border-t border-teal-600/50">
                          {nextTierInfo.isMaxTier ? (
                            <div className="flex items-center text-yellow-300">
                              <Star className="h-4 w-4 mr-1" />
                              <span className="text-sm font-medium">
                                {nextTierInfo.isLifetime ? 'Highest Lifetime Tier Achieved!' : 'Highest Tier Achieved!'}
                              </span>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center text-teal-200">
                                <TrendingUp className="h-4 w-4 mr-1" />
                                <span className="text-sm">Next Tier: <span className="font-medium text-yellow-300">{nextTierInfo.nextTierName}</span></span>
                              </div>
                              <div className="text-sm text-teal-300">
                                <span className="font-medium text-accent-500">{nextTierInfo.pointsNeeded?.toLocaleString()}</span> more {nextTierInfo.unit || 'points'} needed
                              </div>
                              {nextTierInfo.sailingsNeeded !== undefined && (
                                <div className="text-sm text-teal-300">
                                  <span className="font-medium text-accent-500">{nextTierInfo.sailingsNeeded}</span> more sailing{nextTierInfo.sailingsNeeded !== 1 ? 's' : ''} needed
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-teal-200 text-sm">
                        No loyalty status recorded
                      </p>
                      <Link
                        to="/profile"
                        className="inline-flex items-center text-sm text-yellow-300 hover:text-yellow-200 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Update in Profile
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Casino Comps Section - Only show if user has cruises and at least one is tagged as casino comp */}
      {cruises && cruises.length > 0 && stats.casinoComps.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-md p-6 text-white"
        >
          <div className="flex items-center mb-4">
            <Spade className="h-6 w-6 mr-2" />
            <h3 className="text-xl font-semibold">Casino Comps</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="text-yellow-100">Upcoming Comps</div>
              <div className="text-2xl font-bold">{stats.casinoComps.upcoming}</div>
              <div className="text-lg font-semibold">{formatCurrency(stats.casinoComps.upcomingValue)}</div>
            </div>
            
            <div className="space-y-2">
              <div className="text-yellow-100">Past Comps</div>
              <div className="text-2xl font-bold">{stats.casinoComps.past}</div>
              <div className="text-lg font-semibold">{formatCurrency(stats.casinoComps.pastValue)}</div>
            </div>
            
            <div className="space-y-2">
              <div className="text-yellow-100">Total Value</div>
              <div className="text-2xl font-bold">{stats.casinoComps.total}</div>
              <div className="text-lg font-semibold">{formatCurrency(stats.casinoComps.totalValue)}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Annual Gambling Summary */}
      {(stats.yearlyGambling.wins > 0 || stats.yearlyGambling.losses > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-700 to-purple-900 rounded-lg shadow-md p-6 text-white"
        >
          <div className="flex items-center mb-4">
            <Calculator className="h-6 w-6 mr-2" />
            <h3 className="text-xl font-semibold">{currentYear} Gambling Results</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center text-purple-100">
                <TrendingUp className="h-5 w-5 mr-2 text-green-300" />
                <span>Yearly Wins</span>
              </div>
              <div className="text-2xl font-bold text-green-300">{formatCurrency(stats.yearlyGambling.wins)}</div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center text-purple-100">
                <TrendingDown className="h-5 w-5 mr-2 text-red-300" />
                <span>Yearly Losses</span>
              </div>
              <div className="text-2xl font-bold text-red-300">{formatCurrency(stats.yearlyGambling.losses)}</div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center text-purple-100">
                <Calculator className="h-5 w-5 mr-2 text-yellow-300" />
                <span>Net Result</span>
              </div>
              <div className={`text-2xl font-bold ${
                stats.yearlyGambling.net >= 0 ? 'text-green-300' : 'text-red-300'
              }`}>
                {stats.yearlyGambling.net >= 0 ? '+' : ''}{formatCurrency(stats.yearlyGambling.net)}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Annual Financial Summary */}
      {(stats.annualFinancials.totalExpenses > 0 || stats.annualFinancials.cruiseCompValue > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-lg shadow-md p-6 text-white"
        >
          <div className="flex items-center mb-4">
            <Wallet className="h-6 w-6 mr-2" />
            <h3 className="text-xl font-semibold">{currentYear} Financial Summary</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.annualFinancials.cruiseCompValue > 0 && (
              <div className="space-y-2">
                <div className="flex items-center text-emerald-100">
                  <Spade className="h-5 w-5 mr-2 text-yellow-300" />
                  <span>Comp Value</span>
                </div>
                <div className="text-xl font-bold text-yellow-300">
                  {formatCurrency(stats.annualFinancials.cruiseCompValue)}
                </div>
                <div className="text-sm text-emerald-200">Casino comps earned</div>
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex items-center text-emerald-100">
                <CurrencyIcon className="h-5 w-5 mr-2 text-blue-300" />
                <span>Total Expenses</span>
              </div>
              <div className="text-xl font-bold text-blue-300">
                {formatCurrency(stats.annualFinancials.totalExpenses)}
              </div>
              <div className="text-sm text-emerald-200">Out-of-pocket costs</div>
            </div>
            
            {(stats.yearlyGambling.wins > 0 || stats.yearlyGambling.losses > 0) && (
              <div className="space-y-2">
                <div className="flex items-center text-emerald-100">
                  <Calculator className="h-5 w-5 mr-2 text-purple-300" />
                  <span>Gambling Net</span>
                </div>
                <div className={`text-xl font-bold ${
                  stats.yearlyGambling.net >= 0 ? 'text-green-300' : 'text-red-300'
                }`}>
                  {stats.yearlyGambling.net >= 0 ? '+' : ''}{formatCurrency(stats.yearlyGambling.net)}
                </div>
                <div className="text-sm text-emerald-200">Casino results</div>
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex items-center text-emerald-100">
                <PiggyBank className="h-5 w-5 mr-2 text-emerald-300" />
                <span>Annual Net Cost</span>
              </div>
              <div className={`text-xl font-bold ${
                stats.annualFinancials.netCost <= 0 ? 'text-green-300' : 'text-emerald-300'
              }`}>
                {stats.annualFinancials.netCost <= 0 ? '+' : ''}{formatCurrency(Math.abs(stats.annualFinancials.netCost))}
              </div>
              <div className="text-sm text-emerald-200">
                {stats.annualFinancials.netCost <= 0 ? 'Net profit' : 'After gambling'}
              </div>
            </div>
          </div>
          
          {/* Additional context */}
          <div className="mt-6 pt-4 border-t border-emerald-600/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-emerald-800/30 rounded-lg p-3">
                <h4 className="font-medium text-emerald-200 mb-2">Calculation Method</h4>
                <p className="text-emerald-100 text-xs leading-relaxed">
                  Annual Net Cost = Total Expenses - Gambling Net Result
                  {stats.annualFinancials.cruiseCompValue > 0 && (
                    <><br />Casino comps are tracked separately as earned value</>
                  )}
                </p>
              </div>
              <div className="bg-emerald-800/30 rounded-lg p-3">
                <h4 className="font-medium text-emerald-200 mb-2">Includes</h4>
                <p className="text-emerald-100 text-xs leading-relaxed">
                  Cruise costs, port fees, additional costs, onboard expenses, and gambling results from completed {currentYear} cruises
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>

  );
};

export default Highlights;